import { useEffect, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import Colors from "../styles/Colors.json";
import "./ProjectsPageV1.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import config from "../configLoader";
import ProjectSelector from "../components/ProjectSelector";
import { Project } from "../interfaces/Project";
import ProjectsAPI from "../api/projects";
import { ProjectComplete } from "../interfaces/ProjectCompleteInterface";
import ProjectCompleteDisplay from "../components/ProjectCompleteDisplay";
import ActionButton from "../components/ActionButton";
import { JobStatusEnum } from "../interfaces/V2Interface";
import { getJobStatus } from "../api/vistaBatchExport";
import ProjectMapPreview from "../components/ProjectMapPreview";
import ProjectGeolocationModalV1 from "../components/ProjectGeolocationModalV1";
import { projectHasMapCoords } from "../utils/projectMapCoords";

const ProjectsPageV1: React.FC = () => {
    const { setPage, createPopup, setLoading, userRole, projectIntent, setProjectIntent } = useSiteContext();
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [projectData, setProjectData] = useState<ProjectComplete | null>(null);
    const [projectMapModalOpen, setProjectMapModalOpen] = useState(false);

    const canViewProjectMap = userRole.viewProjectMap === true;
    const canEditProjectMap = userRole.editProjectMap === true;
    /** Location text column + map column require at least one map permission flag from the API. */
    const canAccessProjectMapUi = canViewProjectMap || canEditProjectMap;

    const formatLoc = (v: string | null | undefined) =>
        v != null && String(v).trim() !== "" ? v : "—";

    const getPieceSheetErrorMessage = async (error: any) => {
        const errorData = error?.response?.data;
        if (errorData instanceof Blob) {
            try {
                const text = await errorData.text();
                if (!text) return "Failed to download piece sheet.";
                const parsed = JSON.parse(text) as { message?: string };
                return parsed?.message || "Failed to download piece sheet.";
            } catch {
                return "Failed to download piece sheet.";
            }
        }
        return error?.response?.data?.message || "Failed to download piece sheet.";
    };

    // Check if feature is enabled
    useEffect(() => {
        if (!config.showProjectsPage) {
            createPopup("Access Denied", "This feature is not available.");
            setPage(Page.Dashboard);
            return;
        }
    }, [setPage, createPopup]);

    // Handle project selection and fetch complete project data
    const handleProjectSelect = async (project: Project) => {
        setProjectMapModalOpen(false);
        setSelectedProject(project);
        setProjectData(null); // Clear previous data
        
        try {
            setLoading(true);
            const response = await ProjectsAPI.getCompleteProject(project.projectID);
            setProjectData(response.data);
        } catch (error) {
            console.error("Error fetching complete project:", error);
            createPopup("Error", "Failed to load project data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Project Map "Open in Projects" intent — look the project up and open it.
    useEffect(() => {
        if (!projectIntent) return;
        const intent = projectIntent;
        setProjectIntent(null);
        (async () => {
            try {
                setLoading(true);
                const results = await ProjectsAPI.typeahead(intent.altProjectID);
                const found = results.find((r) => r.projectID === intent.projectID);
                if (found) {
                    await handleProjectSelect(found);
                } else {
                    createPopup("Error", `Project ${intent.altProjectID} not found.`);
                }
            } catch {
                createPopup("Error", "Failed to open project from the map.");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectIntent]);

    const handleSyncPieces = async () => {
        if (!selectedProject?.altProjectID2) return;
        try {
            setLoading(true);
            const response = await ProjectsAPI.projectAddUpdatePieces(
                selectedProject.altProjectID,
                Number(selectedProject.altProjectID2)
            );
            const data = response.data as {
                success: boolean;
                message: string;
                updates?: { piecesAdded: number; piecesUpdated: number; piecesRemoved: number };
            };
            if (data.success) {
                let message = data.message;
                if (data.updates) {
                    message += `\n\nPieces Added: ${data.updates.piecesAdded}\nPieces Updated: ${data.updates.piecesUpdated}\nPieces Removed: ${data.updates.piecesRemoved}`;
                }
                createPopup("Success", message);
                const refreshResponse = await ProjectsAPI.getCompleteProject(selectedProject.projectID);
                setProjectData(refreshResponse.data);
            } else {
                createPopup("Error", data.message || "Sync failed.");
            }
        } catch (error) {
            console.error("Error syncing pieces:", error);
            createPopup("Error", "Failed to sync pieces. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshPieceCache = async () => {
        try {
            setLoading(true);
            const { jobID } = await ProjectsAPI.refreshPieceCache();
            const pollIntervalMs = 5000;
            while (true) {
                const jobStatus = await getJobStatus(jobID);
                if (jobStatus.status === JobStatusEnum.COMPLETED) {
                    const json = JSON.stringify(jobStatus.resultData ?? {}, null, 2);
                    createPopup(
                        "Sync JC Pieceworks Cache",
                        `Response:\n\n${json}`
                    );
                    break;
                } else if (jobStatus.status === JobStatusEnum.FAILED) {
                    const resultJson = jobStatus.resultData != null
                        ? `\n\n${JSON.stringify(jobStatus.resultData, null, 2)}`
                        : "";
                    createPopup(
                        "Error",
                        `Piece cache sync failed.${resultJson}`
                    );
                    break;
                }
                await new Promise((r) => setTimeout(r, pollIntervalMs));
            }
        } catch (error) {
            console.error("Error refreshing piece cache:", error);
            createPopup("Error", "Failed to refresh piece cache. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPieceSheet = async () => {
        if (!selectedProject) return;
        try {
            setLoading(true);
            const { blob } = await ProjectsAPI.downloadPieceSheet(selectedProject.projectID);
            const today = new Date();
            const month = today.getMonth() + 1;
            const day = today.getDate();
            const year = today.getFullYear();
            const fileName = `PSheet-${selectedProject.altProjectID}-${month}-${day}-${year}.xlsx`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading piece sheet:", error);
            const message = await getPieceSheetErrorMessage(error);
            createPopup("Error", message);
        } finally {
            setLoading(false);
        }
    };

    if (!config.showProjectsPage) {
        return (
            <PageWrapper title="Projects" backgroundColor={Colors.White}>
                <div style={{ padding: "20px", textAlign: "center" }}>
                    <h2>Access Denied</h2>
                    <p>This feature is not available.</p>
                </div>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper title="Projects" backgroundColor={Colors.White}>
            <div style={{ padding: "20px" }}>
                <div style={{ marginBottom: "20px" }}>
                    <ProjectSelector
                        onProjectSelect={handleProjectSelect}
                        useSearchAll={true}
                    />
                </div>
                {selectedProject && (
                    <div style={{ marginTop: "20px", padding: "15px", backgroundColor: config.secondaryColor, borderRadius: "8px", marginBottom: "20px" }}>
                        {((config.enableSyncPieces && userRole.admin) || (config.downloadPSheet && projectData)) && (
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
                                    {config.downloadPSheet && projectData && (
                                        <ActionButton
                                            onAction={handleDownloadPieceSheet}
                                            isEnabled={true}
                                            title="Download PSheet"
                                        />
                                    )}
                                    {config.enableSyncPieces && userRole.admin && (
                                        <>
                                            <ActionButton
                                                onAction={handleSyncPieces}
                                                isEnabled={!!selectedProject.altProjectID2}
                                                title="Sync Pieces"
                                            />
                                            <ActionButton
                                                onAction={handleRefreshPieceCache}
                                                isEnabled={true}
                                                title="Sync JC Pieceworks Cache"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="projects-page-selected-columns">
                            <div className="projects-page-selected-col projects-page-selected-col-project">
                                <p><strong>Project ID:</strong> {selectedProject.altProjectID}</p>
                                <p><strong>Name:</strong> {selectedProject.name}</p>
                                <p><strong>Active:</strong> {selectedProject.active ? "Yes" : "No"}</p>
                                {projectData?.supervisors && projectData.supervisors.length > 0 && (
                                    <div style={{ marginTop: 4 }}>
                                        <strong>Supervisors:</strong>
                                        {projectData.supervisors.map(s => (
                                            <div key={s.employeeID}>{s.name.first} {s.name.last}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {canAccessProjectMapUi && (
                                <>
                                    <div className="projects-page-selected-col projects-page-selected-col-location">
                                        <p>
                                            <strong>Address:</strong> {formatLoc(selectedProject.address)}
                                        </p>
                                        <p>
                                            <strong>City:</strong> {formatLoc(selectedProject.city)}{" "}
                                            <strong>State:</strong> {formatLoc(selectedProject.state)}{" "}
                                            <strong>ZIP:</strong> {formatLoc(selectedProject.zip)}
                                        </p>
                                        <p>
                                            <strong>Cross streets:</strong> {formatLoc(selectedProject.crossStreets)}
                                        </p>
                                        {projectHasMapCoords(selectedProject) ? (
                                            <p>
                                                <strong>Latitude:</strong>{" "}
                                                {(selectedProject.latitude as number).toFixed(6)}
                                                {" "}
                                                <strong>Longitude:</strong>{" "}
                                                {(selectedProject.longitude as number).toFixed(6)}
                                            </p>
                                        ) : (
                                            <p className="projects-page-no-map">No map location</p>
                                        )}
                                        {canEditProjectMap && !projectHasMapCoords(selectedProject) && (
                                            <div style={{ marginTop: 10 }}>
                                                <ActionButton
                                                    title="Set map location"
                                                    onAction={() => setProjectMapModalOpen(true)}
                                                    isEnabled={true}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {projectHasMapCoords(selectedProject) && (
                                        <div className="projects-page-selected-col projects-page-selected-col-map">
                                            <ProjectMapPreview
                                                latitude={selectedProject.latitude as number}
                                                longitude={selectedProject.longitude as number}
                                                radiusMiles={
                                                    typeof selectedProject.radiusMiles === "number" &&
                                                    selectedProject.radiusMiles > 0
                                                        ? selectedProject.radiusMiles
                                                        : 0
                                                }
                                                onOpen={() => setProjectMapModalOpen(true)}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
                {selectedProject && canAccessProjectMapUi && (
                    <ProjectGeolocationModalV1
                        isOpen={projectMapModalOpen}
                        onClose={() => setProjectMapModalOpen(false)}
                        project={selectedProject}
                        canEdit={canEditProjectMap}
                        onSaved={(updated) => setSelectedProject(updated)}
                        createPopup={createPopup}
                        setLoading={setLoading}
                    />
                )}
                {projectData && (
                    <ProjectCompleteDisplay projectData={projectData} />
                )}
            </div>
        </PageWrapper>
    );
};

export default ProjectsPageV1;

